"use client";
/**
 * MessageBubble — renders a single chat message (user or assistant).
 * Track C2.
 */
import { useState, useRef } from "react";
import {
  User,
  Copy,
  Check,
  ThumbsUp,
  ThumbsDown,
  ChevronDown,
  ChevronUp,
  Sparkles,
// <<<<<<< HEAD
  Plus,
  FileText,
  Loader2,
  Pencil,
  Printer,
} from "lucide-react";
import { MarkdownRenderer } from "./MarkdownRenderer";
import type { Message } from "@/modes/ask/types";
import { mermaidSourceToPng } from "@/modes/draft/utils/mermaid";
import { SenseiMark } from "@/shared/components/layout/SenseiMark";
import { useAuthContext } from "@/platform/auth/AuthContext";
import { exportAskResponseToDraft } from "@/modes/ask/lib/askToDraft";
// =======
//   FileText,
//   Loader2,
// } from "lucide-react";
// import { MarkdownRenderer } from "./MarkdownRenderer";
// import type { Message } from "@/modes/ask/types";
// import { useAuthContext } from "@/platform/auth/AuthContext";
// import { exportAskResponseToDraft } from "@/modes/ask/lib/askToDraft";
// >>>>>>> origin/master-imran
import { SenseiMarkLoader } from "@/shared/components/layout/SenseiMarkLoader";
import { SenseiMarkStill } from "@/shared/components/layout/SenseiMarkStill";
import Link from "next/link";

interface MessageBubbleProps {
  message:     Message;
  /** True only for the last assistant message while streaming */
  isStreaming?: boolean;
  /** Current conversation session ID — needed for export-to-draft source metadata */
  sessionId?: string;
  /** When set, every occurrence of this string inside the message is highlighted */
  highlightQuery?: string;
  /** 0-based ordinal of the search occurrence to emphasize within this message */
  activeOccurrence?: number;
  /** Show an edit (pencil) button on user messages — invoked with the message to edit */
  onEditMessage?: (message: Message) => void;
  /** Show a per-message print button on assistant messages */
  onPrintMessage?: (message: Message) => void;
}

// <<<<<<< HEAD
// ─── Upgrade CTA card ─────────────────────────────────────────────────────────

function UpgradeCTA({ content }: { content: string }) {
  const isPro = !content.toLowerCase().includes("upgrade");

  // ── Pro: subtle inline notice, no selling needed ──────────────────────────
  if (isPro) {
    return (
      <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-border/60 bg-muted/40 text-sm text-muted-foreground">
        <span className="text-base leading-none select-none flex-shrink-0">💬</span>
        <span className="flex-1">You&apos;ve reached the token limit for this conversation. Start a new one to keep chatting.</span>
        <a
          href="/ask"
          className="flex-shrink-0"
          style={{
            display:        "inline-flex",
            alignItems:     "center",
            gap:            8,
            padding:        "10px 18px",
            borderRadius:   100,
            background:     "linear-gradient(135deg, #5B6CFF, #EC4899)",
            color:          "#fff",
            fontSize:       14,
            fontWeight:     600,
            textDecoration: "none",
            whiteSpace:     "nowrap",
            boxShadow:      "0 4px 18px rgba(91,108,255,0.3)",
          }}
        >
          <Plus size={14} strokeWidth={2.5} />
          New Chat
        </a>
      </div>
    );
  }

  // ── Free: green CTA card with upgrade button ───────────────────────────────
  return (
    <div
      className="rounded-xl overflow-hidden"
      style={{ background: "linear-gradient(135deg, #10b981 0%, #059669 100%)" }}
    >
      <div className="px-4 py-2 flex items-start gap-1">
        {/* <span className="text-2xl leading-none select-none flex-shrink-0">⚡</span> */}
        <div className="flex-1 min-w-0">
          <p className="text-white font-semibold text-sm leading-snug mb-1">
            Upgrade to sensei. Pro
          </p>
          <p className="text-emerald-100 text-xs leading-relaxed">
            {content}
          </p>
          <a
            href="/pricing"
            className="mt-1 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-all hover:opacity-90 active:scale-95"
            style={{ background: "white", color: "#059669" }}
          >
            Upgrade Now →
          </a>
        </div>
      </div>
    </div>
  );
}

// ─── Session completed card ───────────────────────────────────────────────────
// Shown when a conversation reaches its token limit: "session completed" and a
// new conversation starts automatically in the same window.

function SessionCompleteCard({ content }: { content: string }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-border/60 bg-muted/40 text-sm text-muted-foreground">
      <span className="text-base leading-none select-none flex-shrink-0">✅</span>
      <div className="flex-1 min-w-0">
        <p className="text-foreground font-semibold text-sm leading-snug mb-0.5">
          Session completed
        </p>
        <p className="text-xs leading-relaxed">{content}</p>
      </div>
      <Link
        href="/ask"
        className="flex-shrink-0"
        style={{
          display:        "inline-flex",
          alignItems:     "center",
          gap:            8,
          padding:        "10px 18px",
          borderRadius:   100,
          background:     "linear-gradient(135deg, #5B6CFF, #EC4899)",
          color:          "#fff",
          fontSize:       14,
          fontWeight:     600,
          textDecoration: "none",
          whiteSpace:     "nowrap",
          boxShadow:      "0 4px 18px rgba(91,108,255,0.3)",
        }}
      >
        <Plus size={14} strokeWidth={2.5} />
        New Chat
      </Link>
    </div>
  );
}

export function MessageBubble({ message, isStreaming = false, sessionId, highlightQuery, activeOccurrence, onEditMessage, onPrintMessage }: MessageBubbleProps) {
  const [copied,     setCopied]     = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [feedback,   setFeedback]   = useState<"like" | "dislike" | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const messageContentRef = useRef<HTMLDivElement>(null);
  const { user } = useAuthContext();
// =======
// export function MessageBubble({ message, isStreaming = false, sessionId }: MessageBubbleProps) {
//   const [copied,     setCopied]     = useState(false);
//   const [isExpanded, setIsExpanded] = useState(false);
//   const [feedback,   setFeedback]   = useState<"like" | "dislike" | null>(null);
//   const [isExporting, setIsExporting] = useState(false);

//   const { user } = useAuthContext();
// >>>>>>> origin/master-imran

  const isUser = message.role === "user";

  // Detect HTML content (backward-compat with old messages)
  const isHtml = isUser ? true : false;

  // Truncate long user messages
  const shouldTruncate = isUser && message.content.replace(/<[^>]*>/g, "").length > 200;
  const displayContent =
    shouldTruncate && !isExpanded
      ? message.content.replace(/<[^>]*>/g, "").substring(0, 200) + "..."
      : message.content;

  // ── Search highlighting ──────────────────────────────────────────────────

  /** Wrap every occurrence of `query` in <mark> so it stands out visually.
   *  The occurrence at `activeOcc` (0-based ordinal) is emphasized strongly. */
  const applyHighlight = (raw: string, activeOcc?: number): string => {
    const q = highlightQuery?.trim();
    if (!q) return raw;
    const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    let idx = -1;
    return raw.replace(
      new RegExp(`(${escaped})`, "gi"),
      (match: string, p1: string): string => {
        idx += 1;
        if (activeOcc !== undefined && idx === activeOcc) {
          return `<mark style="background:#6366f1;color:#fff;border-radius:2px;padding:0 2px;">${p1}</mark>`;
        }
        return `<mark style="background:rgba(59,130,246,0.25);color:inherit;border-radius:2px;padding:0 2px;">${p1}</mark>`;
      }
    );
  };

  // ── Copy ──────────────────────────────────────────────────────────────────

  /**
   * Build a copy-ready HTML string from the message content.
   * Mermaid diagrams are replaced with <img> PNG data URLs — the only format
   * paste targets (Google Docs, Word, Notion, email) reliably accept.
   */
  const buildCopyHtml = async (container: HTMLDivElement): Promise<{ html: string; text: string }> => {
    const clone = container.cloneNode(true) as HTMLDivElement;

    // Strip UI chrome (zoom button, download buttons, etc.)
    clone.querySelectorAll("button, [aria-label]").forEach((el) => el.remove());

    // Extract mermaid source blocks from raw markdown
    const mermaidSources: string[] = [];
    const mermaidRe = /```mermaid\s*\n([\s\S]*?)```/gi;
    let m;
    while ((m = mermaidRe.exec(message.content)) !== null) {
      mermaidSources.push(m[1].trim());
    }

    if (mermaidSources.length > 0) {
      // Render each source to a PNG data URL
      const pngs = await Promise.all(
        mermaidSources.map((src, i) => mermaidSourceToPng(src, `copy-${Date.now()}-${i}`))
      );

      // Replace .mermaid-container divs in the clone with <img> tags
      const containers = Array.from(clone.querySelectorAll(".mermaid-container"));
      containers.forEach((el, i) => {
        const png = pngs[i];
        const wrapper = document.createElement("div");
        wrapper.style.cssText = "margin: 12px 0; display: block;";
        if (png) {
          const img = document.createElement("img");
          img.src = png;
          img.alt = "Mermaid diagram";
          img.style.cssText = "max-width: 100%; height: auto; display: block;";
          wrapper.appendChild(img);
        }
        // Walk up to replace the whole relative wrapper (which held the zoom button)
        const outerWrapper = el.closest(".relative") ?? el;
        outerWrapper.replaceWith(wrapper);
      });
    }

    return {
      html: clone.innerHTML,
      text: clone.textContent || clone.innerText || "",
    };
  };

  const handleCopy = async () => {
    try {
      let htmlContent = "";
      let plainText = "";

      // For AI messages, grab the rendered HTML from the DOM (with diagrams as images)
      if (!isUser && messageContentRef.current) {
        const built = await buildCopyHtml(messageContentRef.current);
        htmlContent = built.html;
        plainText   = built.text;
      } else {
        // For user messages, use the stored content
        htmlContent = message.content;
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = message.content;
        plainText = tempDiv.textContent || tempDiv.innerText || "";
      }

      // Tier 1 — modern ClipboardItem with both HTML + plain text
      if (navigator.clipboard && navigator.clipboard.write) {
        try {
          const htmlBlob = new Blob([htmlContent], { type: "text/html" });
          const textBlob = new Blob([plainText], { type: "text/plain" });
          await navigator.clipboard.write([
            new ClipboardItem({ "text/html": htmlBlob, "text/plain": textBlob }),
          ]);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
          return;
        } catch (e) {
          console.warn("ClipboardItem failed, trying fallback:", e);
        }
      }

      // Tier 2 — execCommand with synthetic copy event (preserves formatting)
      const listener = (e: ClipboardEvent) => {
        if (e.clipboardData) {
          e.clipboardData.setData("text/html", htmlContent);
          e.clipboardData.setData("text/plain", plainText);
          e.preventDefault();
        }
      };
      document.addEventListener("copy", listener);
      document.execCommand("copy");
      document.removeEventListener("copy", listener);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy:", error);

      // Tier 3 — plain text only as last resort
      try {
        let plainText = "";
        if (!isUser && messageContentRef.current) {
          plainText =
            messageContentRef.current.textContent ||
            messageContentRef.current.innerText ||
            "";
        } else {
          const tempDiv = document.createElement("div");
          tempDiv.innerHTML = message.content;
          plainText = tempDiv.textContent || tempDiv.innerText || "";
        }

        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(plainText);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } else {
          alert("Copy failed. Please select and copy manually.");
        }
      } catch (fallbackError) {
        console.error("All copy methods failed:", fallbackError);
        alert("Copy failed. Please select and copy manually.");
      }
    }
  };

  // ── Export to Draft ─────────────────────────────────────────────────────

  const handleExportToDraft = async () => {
    if (!user) {
      window.alert("Please login to export to Draft");
      return;
    }

    if (isExporting) return;

    setIsExporting(true);

    try {
      const result = await exportAskResponseToDraft(user.uid, {
        messageContent: message.content,
        conversationTitle: undefined,
        userQuery: undefined,
        messageId: message.id,
        sessionId,
        mode: "doc",
      });

      // Navigate to the new draft (full reload avoids mermaid DOM conflicts)
      window.location.href = `/draft/${result.draftId}`;
    } catch (err) {
      console.error("Export to Draft failed:", err);
      window.alert("Failed to export to Draft. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  // ── Suggestion chip click ─────────────────────────────────────────────────

  const handleSuggestionClick = (prompt: string) => {
    window.dispatchEvent(
      new CustomEvent("selectSuggestedPrompt", { detail: { prompt, text: prompt } })
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div
      className={`flex gap-3 group animate-fade-in hover:bg-white/5 rounded-2xl p-2 transition-all duration-300 ${
        isUser ? "flex-row-reverse" : "flex-row"
      }`}
    >
      {/* Avatar */}
      <div
        className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${
          isUser
            ? "bg-gradient-to-br from-[#5B6CFF] to-[#EC4899]"
            : "bg-transparent"
        }`}
      >
        {isUser ? (
          <User className="h-4 w-4 text-white" />
        ) : isStreaming ? (
          <SenseiMarkLoader size={36} />
        ) : (
          <SenseiMarkStill size={36} />
        )}
      </div>

      {/* Content */}
      <div
        className={`flex-1 max-w-full ${
          isUser ? "flex flex-col items-end" : ""
        }`}
      >
        {/* Attachments — shown above content */}
        {message.attachments && message.attachments.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {message.attachments.map((att, i) => (
              <span
                key={att.id ?? i}
                className={`text-xs px-2 py-1 rounded-lg flex items-center gap-1.5 ${
                  isUser
                    ? "bg-indigo-600/70 text-white border border-indigo-300/40 backdrop-blur-sm"
                    : "bg-muted/50 text-muted-foreground"
                }`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-current" />
                📎 {att.name}
              </span>
            ))}
          </div>
        )}

        {/* Bubble */}
        <div
          className={`rounded-2xl px-4 py-3 shadow-sm ${
            isUser
              ? "bg-gradient-to-br from-[#5B6CFF] to-[#EC4899] text-white rounded-tr-md"
              : "bg-card border border-border/50 rounded-tl-md"
          }`}
        >
          <div className={`text-xs font-medium mb-1.5 opacity-70 ${isUser ? "text-white" : "text-muted-foreground"}`}>
            {isUser ? "You" : "sensei."}
          </div>

          <div ref={messageContentRef} className={`text-sm leading-relaxed ${isUser ? "text-white" : "text-foreground themessage"}`}>
            {message.type === "session_completed" ? (
              <SessionCompleteCard content={message.content} />
            ) : message.type === "upgrade" ? (
              <UpgradeCTA content={message.content} />
            ) : isUser || isHtml ? (
              <div
                dangerouslySetInnerHTML={{ __html: applyHighlight(displayContent, activeOccurrence) }}
              />
            ) : (
              /* Key flip on streaming→complete forces remount to prevent mermaid DOM conflicts */
              <MarkdownRenderer
                key={isStreaming ? "streaming" : "complete"}
                content={applyHighlight(message.content, activeOccurrence)}
                isStreaming={isStreaming}
              />
            )}
          </div>

          {/* Expand / collapse for long user messages */}
          {shouldTruncate && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className={`mt-2 flex items-center gap-1 text-xs transition-colors ${
                isUser
                  ? "text-indigo-100 hover:text-white"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {isExpanded ? (
                <><span>Show less</span><ChevronUp className="h-3 w-3" /></>
              ) : (
                <><span>Show more</span><ChevronDown className="h-3 w-3" /></>
              )}
            </button>
          )}
        </div>

        {/* Suggestion chips */}
        {message.role === "assistant" &&
          !isStreaming &&
          message.suggestions &&
          message.suggestions.length > 0 && (
            <div className="mt-3">
              <p className="text-xs text-muted-foreground mb-2 font-medium flex items-center gap-1.5">
                <Sparkles className="h-3 w-3" />
                Suggested Actions
              </p>
              <div className="flex flex-wrap gap-1.5">
                {message.suggestions.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => handleSuggestionClick(s)}
                    style={{ animationDelay: `${i * 100}ms` }}
                    className="px-3 py-1.5 text-xs rounded-lg bg-muted hover:bg-muted/80 border border-border/50 hover:border-indigo-500/30 hover:shadow-md transition-all animate-fade-in focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

        {/* Action row (assistant only) */}
        {!isUser && (
          <div className="flex items-center gap-0.5 mt-2">
            <button
              onClick={handleCopy}
              className="h-7 w-7 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all flex items-center justify-center"
              aria-label="Copy message"
            >
              {copied ? (
                <Check className="h-3.5 w-3.5 text-green-500" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
            </button>
            <button
              onClick={handleExportToDraft}
              disabled={isExporting}
              title="Export to Draft"
              className="h-7 w-7 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all flex items-center justify-center disabled:opacity-50"
              aria-label="Export to Draft"
            >
              {isExporting ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <FileText className="h-3.5 w-3.5" />
              )}
            </button>
            {onPrintMessage && (
              <button
                onClick={() => onPrintMessage(message)}
                title="Print this answer"
                aria-label="Print this answer"
                className="h-7 w-7 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all flex items-center justify-center"
              >
                <Printer className="h-3.5 w-3.5" />
              </button>
            )}
            <button
              onClick={() => setFeedback(feedback === "like" ? null : "like")}
              className={`h-7 w-7 rounded-lg transition-all flex items-center justify-center ${
                feedback === "like"
                  ? "text-green-400"
                  : "text-muted-foreground hover:text-green-400"
              }`}
              aria-label="Like"
            >
              <ThumbsUp className={`h-3.5 w-3.5 ${feedback === "like" ? "fill-current" : ""}`} />
            </button>
            <button
              onClick={() => setFeedback(feedback === "dislike" ? null : "dislike")}
              className={`h-7 w-7 rounded-lg transition-all flex items-center justify-center ${
                feedback === "dislike"
                  ? "text-red-400"
                  : "text-muted-foreground hover:text-red-400"
              }`}
              aria-label="Dislike"
            >
              <ThumbsDown className={`h-3.5 w-3.5 ${feedback === "dislike" ? "fill-current" : ""}`} />
            </button>
          </div>
        )}

        {isUser && onEditMessage && (
          <div className="flex items-center gap-0.5 mt-1.5 justify-end">
            <button
              onClick={() => onEditMessage(message)}
              title="Edit message"
              aria-label="Edit message"
              className="h-7 w-7 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all flex items-center justify-center"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {/* Timestamp + token usage */}
        <div className="flex flex-col gap-0.5 mt-1.5 px-1">
          <span className="text-[10px] text-muted-foreground">
            {new Date(message.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </span>
          {!isUser && message.token_usage?.total_tokens != null && (
            <span className="text-[10px] text-muted-foreground">
              Tokens: {message.token_usage.total_tokens.toLocaleString()}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
